import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { projectsAPI } from '../services/api';
import { format } from 'date-fns';
import { useAuth } from '../utils/AuthContext';

function ProjectsList() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await projectsAPI.getProjects();
      setProjects(response.data);
    } catch (error) {
      toast.error('Failed to load projects');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) {
      return;
    }

    try {
      await projectsAPI.deleteProject(projectId);
      toast.success('Project deleted successfully!');
      fetchProjects();
    } catch (error) {
      toast.error('Failed to delete project');
      console.error(error);
    }
  };

  const canModify = user?.role === 'Admin' || user?.role === 'QA';

  return (
    <Container fluid style={{ padding: '2rem 3rem' }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="fw-bold mb-0" style={{ fontSize: '28px', color: '#1a1a1a' }}>Projects</h2>
        <Button
          variant="primary"
          onClick={() => navigate('/projects/new')}
          style={{
            borderRadius: '8px',
            padding: '10px 20px',
            fontWeight: '600',
            fontSize: '14px',
            border: 'none'
          }}
        >
          + Create Project
        </Button>
      </div>

      {/* Projects Table */}
      <Card className="border-0 shadow-sm" style={{ borderRadius: '10px' }}>
        <Card.Body style={{ padding: '1.5rem' }}>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead style={{ borderBottom: '2px solid #e0e0e0' }}>
                  <tr>
                    <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Key</th>
                    <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Name</th>
                    <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Description</th>
                    <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Status</th>
                    <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Created</th>
                    <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.length > 0 ? (
                    projects.map((project) => (
                      <tr
                        key={project.id}
                        style={{
                          borderBottom: '1px solid #f5f5f5'
                        }}
                      >
                        <td style={{ fontSize: '14px', padding: '12px 8px' }}>
                          <Badge
                            bg="primary"
                            style={{ borderRadius: '6px', padding: '5px 10px', fontSize: '12px', fontWeight: '600' }}
                          >
                            {project.key}
                          </Badge>
                        </td>
                        <td style={{ fontSize: '14px', padding: '12px 8px', color: '#333', fontWeight: '600' }}>{project.name}</td>
                        <td style={{ fontSize: '14px', padding: '12px 8px', color: '#666' }}>{project.description || '-'}</td>
                        <td style={{ padding: '12px 8px' }}>
                          <Badge
                            bg={project.is_active ? 'success' : 'secondary'}
                            style={{ borderRadius: '12px', padding: '4px 10px', fontSize: '12px', fontWeight: '500' }}
                          >
                            {project.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td style={{ fontSize: '14px', padding: '12px 8px', color: '#999' }}>
                          {format(new Date(project.created_at), 'MMM dd, yyyy')}
                        </td>
                        <td style={{ padding: '12px 8px' }}>
                          <div className="d-flex gap-2">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => navigate(`/projects/${project.id}`)}
                              style={{ borderRadius: '6px', fontSize: '12px', padding: '4px 12px' }}
                            >
                              View Bugs
                            </Button>
                            {canModify && (
                              <>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => navigate(`/projects/${project.id}/edit`)}
                                  style={{ borderRadius: '6px', fontSize: '12px', padding: '4px 12px' }}
                                >
                                  Edit
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleDelete(project.id)}
                                  style={{ borderRadius: '6px', fontSize: '12px', padding: '4px 12px' }}
                                >
                                  Delete
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center text-muted" style={{ padding: '24px' }}>
                        No projects found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}

export default ProjectsList;
